import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RoutePath } from "@/section/router";
import { useGetAchInstanceDocument } from "@/lib/api/ach";
import { PdfDocumentViewer } from "@/section/pdf";
import type { PdfSource } from "@/section/pdf";

type DocumentPreviewType = "sent_document_url" | "final_document_url";

interface PreviewConfig {
  title: string;
  documentKey: DocumentPreviewType;
  description: string;
}

function BaseAchInstancePreviewPage({ title, documentKey, description }: PreviewConfig) {
  const navigate = useNavigate();
  const { templateId, instanceId } = useParams<{ templateId: string; instanceId: string }>();
  const getInstanceDocument = useGetAchInstanceDocument();
  const [pdfSource, setPdfSource] = useState<PdfSource | string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const documentFetcherRef = useRef(getInstanceDocument);

  useEffect(() => {
    documentFetcherRef.current = getInstanceDocument;
  }, [getInstanceDocument]);

  useEffect(() => {
    if (!templateId || !instanceId) {
      setError("Missing ACH template or instance id.");
      setPdfSource(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setPdfSource(null);

    (async () => {
      try {
        const result = await documentFetcherRef.current(templateId, instanceId);
        if (!isMounted) return;
        if (!result.success) {
          throw new Error("Failed to retrieve document metadata.");
        }

        const url = result.data?.[documentKey];
        if (!url) {
          throw new Error("Document is not available for preview.");
        }

        setPdfSource(url);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.message ?? "Unable to load document preview.");
        setPdfSource(null);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [templateId, instanceId, documentKey]);

  const showViewer = !!pdfSource && !isLoading && !error;

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm uppercase text-muted-foreground tracking-wide">ACH</p>
            <h2 className="text-2xl font-bold">{title}</h2>
          </div>
        </div>
      </div>

      {description && (
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
      )}

      {!showViewer && (
        <Card className="flex flex-1 items-center justify-center p-8">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading document…</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center">
              {error ?? "Select an ACH document to view the preview."}
            </div>
          )}
        </Card>
      )}

      {showViewer && (
        <Card className="flex-1 overflow-hidden">
          <PdfDocumentViewer source={pdfSource} width={720} />
        </Card>
      )}
    </div>
  );
}

export function AchInstanceSentPreviewPage() {
  return (
    <BaseAchInstancePreviewPage
      title="Sent Document Preview"
      description="This is the PDF that was originally sent to the signing service."
      documentKey="sent_document_url"
    />
  );
}

export function AchInstanceFinalPreviewPage() {
  return (
    <BaseAchInstancePreviewPage
      title="Final Document Preview"
      description="This is the final PDF returned by the signing service."
      documentKey="final_document_url"
    />
  );
}

